import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { X, Shield, AlertTriangle, Key, UserX } from 'lucide-react';

const UserRoleEditor = ({ user, isOpen, onClose, onRoleChange }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRole, setPendingRole] = useState('');
  const [adminCount, setAdminCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (user && isOpen) {
      setSelectedRole(user.role || 'solo_lectura');
      setError('');
      setSuccess('');
      setShowConfirmation(false);
      checkAdminCount();
    }
  }, [user, isOpen]);

  const checkAdminCount = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'admin'),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(q);
      setAdminCount(querySnapshot.size);
    } catch (error) {
      console.error('Error checking admin count:', error);
    }
  };

  const handleRoleChange = (newRole) => {
    setSelectedRole(newRole);
    setError('');
    setSuccess('');
    
    // Check if trying to remove last admin
    if (user.role === 'admin' && newRole !== 'admin' && adminCount <= 1) {
      setError('No se puede remover el último administrador del sistema');
      return;
    }

    // Show confirmation for admin role changes
    if (newRole === 'admin' || user.role === 'admin') {
      setPendingRole(newRole);
      setShowConfirmation(true);
    } else {
      // Direct role change for non-admin roles
      updateUserRole(newRole);
    }
  };

  const confirmRoleChange = () => {
    setShowConfirmation(false);
    updateUserRole(pendingRole);
  };

  const updateUserRole = async (newRole) => {
    if (!user || newRole === user.role) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'users', user.id), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });

      setSuccess(`Rol actualizado a ${getRoleText(newRole)} exitosamente`);
      
      // Call parent callback to refresh data
      if (onRoleChange) {
        onRoleChange(user.id, newRole);
      }

      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Error al actualizar el rol del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleForcePasswordReset = async () => {
    if (!window.confirm('¿Está seguro de forzar el cambio de contraseña para este usuario?')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // In a real app, you would trigger a password reset email
      await updateDoc(doc(db, 'users', user.id), {
        forcePasswordReset: true,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });

      setSuccess('Se ha forzado el cambio de contraseña. El usuario deberá cambiar su contraseña en el próximo inicio de sesión.');
    } catch (error) {
      console.error('Error forcing password reset:', error);
      setError('Error al forzar el cambio de contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!window.confirm('¿Está seguro de desactivar esta cuenta? El usuario no podrá acceder al sistema.')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'users', user.id), {
        status: 'inactive',
        deactivatedAt: new Date(),
        deactivatedBy: currentUser.uid,
        updatedAt: new Date()
      });

      setSuccess('Cuenta desactivada exitosamente');
      
      if (onRoleChange) {
        onRoleChange(user.id, user.role, 'inactive');
      }

      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error deactivating account:', error);
      setError('Error al desactivar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'shopkeeper':
        return 'Shopkeeper';
      case 'solo_lectura':
        return 'Solo Lectura';
      default:
        return role;
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Acceso completo al sistema, puede gestionar usuarios y configuraciones';
      case 'shopkeeper':
        return 'Puede gestionar órdenes, clientes, equipos y técnicos';
      case 'solo_lectura':
        return 'Solo puede ver información, sin permisos de edición';
      default:
        return '';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isOpen || !user) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Editar Rol de Usuario</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={loading}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* User Info */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Nombre:</span>
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">{user.displayName || 'Sin nombre'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Email:</span>
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">{user.email}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Rol Actual:</span>
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">{getRoleText(user.role)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Fecha de Registro:</span>
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">{formatDate(user.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Error and Success Messages */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-4">
                {success}
              </div>
            )}

            {/* Role Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nuevo Rol
              </label>
              <select
                value={selectedRole}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="solo_lectura">Solo Lectura</option>
                <option value="shopkeeper">Shopkeeper</option>
                <option value="admin">Administrador</option>
              </select>
              
              {/* Role Description */}
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {getRoleDescription(selectedRole)}
              </p>
            </div>

            {/* Additional Actions */}
            <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Acciones Adicionales</h3>
              
              <button
                onClick={handleForcePasswordReset}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-400 rounded-md hover:bg-orange-50 dark:hover:bg-orange-900/20 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-50"
              >
                <Key className="h-4 w-4" />
                <span>Forzar Cambio de Contraseña</span>
              </button>

              <button
                onClick={handleDeactivateAccount}
                disabled={loading || user.id === currentUser.uid}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 disabled:opacity-50"
              >
                <UserX className="h-4 w-4" />
                <span>Desactivar Cuenta</span>
              </button>
              
              {user.id === currentUser.uid && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  No puedes desactivar tu propia cuenta
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 flex items-center justify-center p-4 z-60">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-500 dark:text-orange-400" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Confirmar Cambio de Rol</h3>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                {pendingRole === 'admin' 
                  ? '¿Está seguro de otorgar permisos de administrador a este usuario? Tendrá acceso completo al sistema.'
                  : '¿Está seguro de remover los permisos de administrador de este usuario?'
                }
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmRoleChange}
                  className="flex-1 px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserRoleEditor;